import {
  Box,
  Group,
  TextInput,
  ActionIcon,
  UnstyledButton,
  Text,
  Flex,
  Tooltip,
  Checkbox,
} from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { IconCheck, IconDeviceFloppy, IconPencil } from "@tabler/icons";
import { useState, useEffect, useContext, useMemo } from "react";
import TabMenu from "./TabMenu";
import SaveViewModal from "./components/SaveViewModal";
import { showNotification } from "@mantine/notifications";
import {
  GeneratedChosenIcon,
} from "../../icons/x-symbol-svgrepo-com";
import { getConnectedUserRole } from "../../utils";
import FiltersContext from "./filters/context/FiltersContext";
import { hexToRgba, getFiltersToSave, filtersAreSame } from "../../utils/viewUtils";
import { useCurrentUser } from "../../hooks/useCurrentUser";

const IdeasTab = ({
  view,
  isActive,
  onTabChange,
  upsertViews,
  isGenerated,
  isGlobal,
  setIsGlobal,
  isSubPageView,
  setIsSubPageView,
  activeFilters,
  subPageKey
}: {
  view: any;
  isActive: boolean;
  onTabChange(value: string): void;
  upsertViews: (body: any, func?: (res: any) => void) => void;
  isGenerated: boolean;
  isGlobal: boolean;
  setIsGlobal: (isGlobal: boolean) => void;
  isSubPageView: boolean;
  setIsSubPageView: (isSubPageView: boolean) => void;
  activeFilters: any;
  subPageKey: any;
}) => {
  const { hovered, ref } = useHover();
  const [onEdit, setOnEdit] = useState(false);
  const [viewName, setViewName] = useState(view.name);
  const [saveViewModalOpened, setSaveViewModalOpened] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const currentUser = useCurrentUser();
  const role = getConnectedUserRole(currentUser);
  const canEdit = role === "admin";

  const tabColor: string | null = view?.color || (isGenerated ? '#FF8700' : null);
  const { subPageKey: contextSubPageKey } = useContext(FiltersContext);

  const filtersToSave = useMemo(
    () => getFiltersToSave(activeFilters || [], view.filters || []),
    [activeFilters, view.filters]
  );

  const filtersChanged = useMemo(
    () => {
      const savedViewFilters = (view.filters || []).filter((f: any) => !f.default);
      return !filtersAreSame(filtersToSave, savedViewFilters);
    },
    [filtersToSave, view.filters]
  );

  useEffect(() => {
    setOnEdit(false);
  }, [isActive]);

  const handleOpenSaveViewModal = () => {
    setSaveViewModalOpened(true);
  };

  const handleCloseSaveViewModal = () => {
    setSaveViewModalOpened(false);
  };

  const handleSaveView = async (data: { name: string; tooltip?: string; isGlobal: boolean; isSubPageView: boolean; color?: string; level?: 'ideas' | 'themes' , operator?: 'AND' | 'OR'}) => {
    try {
      await new Promise<void>((resolve, reject) => {
        upsertViews({ 
          name: data.name, 
          _id: view._id, 
          tooltip: data.tooltip || '',
          isGlobal: data.isGlobal,
          subPageKey: contextSubPageKey,
          isSubPageView: data.isSubPageView,
          operator: data.operator || 'AND',
          color: data.color,
          level: data.level,
        }, (res: any) => {
          console.log(res);
          setSaveViewModalOpened(false);
          showNotification({
            title: "Saved successfully!",
            color: "teal",
            message: "view saved successfully",
            icon: <IconPencil size="1.1rem" />,
            autoClose: 1200,
          });
          resolve();
        });
      });
    } catch (error) {
      console.error('Failed to save view:', error);
    }
  };

  return (
    <Box
      className="ideas-tab"
      ref={ref}
      sx={{
        borderRadius: 6,
        backgroundColor: isActive
          ? tabColor
            ? hexToRgba(tabColor, 0.08)
            : "#F0F0FF"
          : "transparent",
        transition: "background-color 0.15s ease",
        ":hover": {
          backgroundColor: isActive
            ? tabColor
              ? hexToRgba(tabColor, 0.12)
              : "#E8E8FC"
            : "#f3f3f6",
        },
      }}
    >
      {onEdit && isActive ? (
        <Group spacing={0}>
          <TextInput
            h={35}
            pl="sm"
            pr={3}
            onChange={(event) => {
              setViewName(event.currentTarget.value);
            }}
            variant="unstyled"
            value={viewName}
            withAsterisk
            w={100}
          />

          {canEdit && (
            <Tooltip
              label="Visible to all"
              position="top"
              withArrow
            >
              <Checkbox
                color="indigo"
                value='visible'
                checked={isGlobal}
                onChange={(event) => setIsGlobal(event.currentTarget.checked)}
                //label="Visible to all"
              />  
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip
              label="Add to sub-pages"
              position="top"
              withArrow 
            >
              <Checkbox
                color="indigo"
                value='subpages'
                checked={isSubPageView}
                onChange={(event) => setIsSubPageView(event.currentTarget.checked)}  
              />
            </Tooltip>
          )}
          <ActionIcon
            color="#5b60e3"
            size="xs"
            mr={5}
            onClick={() => {
              setOnEdit(false);
              upsertViews({ 
                name: viewName, 
                filters: getFiltersToSave(activeFilters || [], view.filters || []), 
                _id: view._id, 
                isGlobal, 
                isSubPageView, 
                subPageKey,
                operator: view.operator || 'AND',
              }, (res: any) => {
                console.log("res",res);
              });
            }}
          >
            <IconCheck size={16} />
          </ActionIcon>
        </Group>
      ) : (
        <Tooltip
          disabled={!isGenerated}
          label={"Automatically generated by Bagel AI"}
        >
          <UnstyledButton
            h={31}
            py={6}
            pl={8}
            pr={isGenerated ? 12 : 8}
            key={view._id}
            value={`${view._id}`}
            onClick={() => onTabChange(view._id)}
            sx={{ position: 'relative', minWidth: 48 }}
          >
            <Group
              spacing={0}
              align="center"
              position="center"
              sx={{ width: '100%' }}
            >
              {isGenerated && (
                <Box mr={3} sx={{ display: 'flex', alignItems: 'center' }}>
                  <GeneratedChosenIcon fill={view.color || "#FF8700"}/>
                </Box>
              )}
              <Tooltip
                label={view.tooltip}
                disabled={!view.tooltip}
                position="top"
                withArrow
              >
                <Text
                  span
                  size={12}
                  fw={500}
                  sx={{
                    whiteSpace: "nowrap",
                    color: isActive ? "#212529" : "#6B7280",
                  }}
                >
                  {view?.name}
                </Text>
              </Tooltip>
              {isActive && filtersChanged && (!view?.isGlobal || canEdit) && (
                <Tooltip label="Unsaved filter changes" position="top" withArrow>
                  <ActionIcon
                    size={16}
                    ml={4}
                    sx={{
                      color: "#5B60E3",
                      "&:hover": { background: "transparent", color: "#474bb0" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      upsertViews({ _id: view._id, filters: filtersToSave, subPageKey });
                    }}
                  >
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                paddingRight: 4,
                paddingLeft: 8,
                borderRadius: '0 6px 6px 0',
                background: hovered
                  ? isActive
                    ? tabColor
                      ? `linear-gradient(to right, transparent, ${hexToRgba(tabColor, 0.12)} 40%)`
                      : 'linear-gradient(to right, transparent, #E8E8FC 40%)'
                    : 'linear-gradient(to right, transparent, #f3f3f6 40%)'
                  : 'none',
                opacity: hovered || menuOpened ? 1 : 0,
                transition: 'opacity 0.1s ease',
              }}
            >
              <TabMenu
                item={view}
                upsertViews={upsertViews}
                onOpenSaveViewModal={handleOpenSaveViewModal}
                onOpenChange={setMenuOpened}
                activeFilters={activeFilters}
              />
            </Box>
          </UnstyledButton>
        </Tooltip>
      )}

      <SaveViewModal
        opened={saveViewModalOpened}
        onClose={handleCloseSaveViewModal}
        onSave={handleSaveView}
        viewName={viewName}
        view={view}
        setViewName={setViewName}
        isGlobal={isGlobal}
        setIsGlobal={setIsGlobal}
        isSubPageView={isSubPageView}
        setIsSubPageView={setIsSubPageView}
        canEdit={canEdit}
      />
    </Box>
  );
};
export default IdeasTab;
